import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeZone } from 'hooks';
import { Period } from '@spike/model';
import { RootState } from 'store';

import Header from '../../components/Header';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';

import styles from './SalesByCategory.module.scss';
import CategoryTable from './CategoryTable';
import SalesByCategoryCharts from './SalesByCategoryCharts';

import { getSalesByCategoryReport } from 'actions/insights/sales/SalesByCategoryReport/SalesByCategoryReportActions';

interface SalesByCategoryProps {
    description?: string;
}

const toDateParam = (m: Moment) => m.format('YYYY-MM-DD');

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

export const SalesByCategory: FunctionComponent<SalesByCategoryProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const dispatch = useDispatch();
    const timeZone = useTimeZone();

    const { report, loading } = useSelector((s: RootState) => s.salesByCategoryReport);

    const [currentTab, setCurrentTab] = useState('table');

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('day'),
            to: now.clone().endOf('day')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [allTime, setAllTime] = useState(false);

    useEffect(() => {
        if (!period) {
            dispatch(getSalesByCategoryReport());
            return;
        }
        dispatch(getSalesByCategoryReport(toDateParam(period.from), toDateParam(period.to)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const url = `/sales_by_category_export_data?marketplace_id=${apiClientWrapper.marketplaceId}`;

    const exportFile = useMemo(() => {
        const categories = report?.categories ?? [];

        return {
            name: `Sales by category report ${marketplace.basics.businessName}.csv`,
            dataInMemory: true,
            headers: ['Category', 'Quantity', 'Gross Sales', 'Discounts', 'Refunds', 'Net Sales', '% of Net Sales'],
            dataToExport: categories.map(c => [
                c.label ?? '—',
                String(c.quantity ?? 0),
                Number(c.grossSales ?? 0).toFixed(2),
                Number(c.discounts ?? 0).toFixed(2),
                Number(c.refunds ?? 0).toFixed(2),
                Number(c.netSales ?? 0).toFixed(2),
                `${(Number(c.netSalesPct ?? 0) * 100).toFixed(2)}%`
            ]) as string[][]
        };
    }, [report, marketplace]);

    const calendar = {
        optionSelected: 'Today'
    };

    const handleChangePeriod = (p: Period | null, isAllTime: boolean) => {
        setAllTime(isAllTime);

        if (isAllTime || !p) {
            setPeriod(null);
            dispatch(getSalesByCategoryReport());
            return;
        }

        setPeriod({ from: p.from, to: p.to });
        dispatch(getSalesByCategoryReport(toDateParam(p.from), toDateParam(p.to)));
    };

    return (
        <div className={styles.container}>
            <div>
                <span className={styles.description}> {props.description} </span>
            </div>

            <div className={styles.categoryContainer}>
                <ToggleTabs
                    options={options}
                    value={currentTab}
                    setValue={setCurrentTab}
                />
            </div>

            <div>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <CategoryTable
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <SalesByCategoryCharts
                        loading={loading}
                        report={report}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesByCategory;
